// @ts-nocheck

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import SolyticsSearchBar from '../../SolyticsSearchBar';
import SolyticsNoDataPresent from '../../SolyticsNoDataPresent';
import SolyticsGrid from '../../SolyticsGrid';
import SolyticsList from '../../SolyticsList';
import SolyticsBox from '../../SolyticsBox';

const SolyticsTableShowHideColumns = ({
  columns = [],
  listVirtualizationThreshold,
  pendingColumnOrder,
  pendingColumnVisibility = {},
  setPendingColumnVisibility,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const alwaysVisibleColumns = useMemo(
    () => columns.filter((col) => col.disableHiding).map((col) => col.id),
    [columns]
  );

  const handleToggleSelectAll = useCallback(() => {
    const allVisible = columns.every((col) => {
      const isVisible =
        pendingColumnVisibility[col.id] !== undefined
          ? pendingColumnVisibility[col.id]
          : true;
      return isVisible;
    });

    const newVisibility = {};
    columns.forEach((col) => {
      if (alwaysVisibleColumns.includes(col.id)) {
        newVisibility[col.id] = true;
        return;
      }
      newVisibility[col.id] = !allVisible;
    });

    setPendingColumnVisibility((prev) => ({
      ...prev,
      ...newVisibility,
    }));
  }, [
    columns,
    pendingColumnVisibility,
    setPendingColumnVisibility,
    alwaysVisibleColumns,
  ]);

  const areAllVisible = useMemo(() => {
    if (columns.length === 0) return false;
    return columns.every((col) => {
      const isVisible =
        pendingColumnVisibility[col.id] !== undefined
          ? pendingColumnVisibility[col.id]
          : true;
      return isVisible;
    });
  }, [columns, pendingColumnVisibility]);

  const areSomeVisible = useMemo(() => {
    if (columns.length === 0) return false;
    const visibleCount = columns.filter((col) => {
      const isVisible =
        pendingColumnVisibility[col.id] !== undefined
          ? pendingColumnVisibility[col.id]
          : true;
      return isVisible;
    }).length;
    return visibleCount > 0 && visibleCount < columns.length;
  }, [columns, pendingColumnVisibility]);

  const orderedColumns = useMemo(() => {
    if (!pendingColumnOrder || pendingColumnOrder.length === 0) return columns;

    const ordered = [];
    pendingColumnOrder.forEach((colId) => {
      const col = columns.find((c) => c.id === colId);
      if (col) ordered.push(col);
    });

    columns.forEach((col) => {
      if (!pendingColumnOrder.includes(col.id)) {
        ordered.push(col);
      }
    });

    return ordered;
  }, [columns, pendingColumnOrder]);

  const filteredColumns = useMemo(() => {
    if (!searchTerm.trim()) return orderedColumns;
    const lowerSearch = searchTerm.toLowerCase();
    return orderedColumns.filter(
      (col) =>
        col.id?.toLowerCase().includes(lowerSearch) ||
        col.header?.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, orderedColumns]);

  const isNoColumnPresent = useMemo(
    () => filteredColumns?.length === 0,
    [filteredColumns]
  );

  // ───────────────────────────────────────────────────────────────────────────
  // Unified List Array: Combines "Select All" + Columns into a single array
  // ───────────────────────────────────────────────────────────────────────────
  const listItems = useMemo(() => {
    const items = [];

    // 1. Inject "Select All" at the top (only if not searching)
    if (!searchTerm) {
      items.push({
        id: 'select-all-columns',
        label: 'Select All',
        checked: areAllVisible,
        indeterminate: areSomeVisible,
        onCheck: handleToggleSelectAll,
        onClick: handleToggleSelectAll,
      });
    }

    // 2. Inject standard columns underneath
    filteredColumns.forEach((column) => {
      const isHideDisabled = Boolean(column.disableHiding);
      const isChecked = isHideDisabled
        ? true
        : pendingColumnVisibility[column.id] !== undefined
          ? pendingColumnVisibility[column.id]
          : true;

      const handleToggle = () => {
        if (isHideDisabled) return;
        setPendingColumnVisibility((prev) => ({
          ...prev,
          [column.id]: !isChecked,
        }));
      };

      items.push({
        id: column.id,
        label: column.header || column.id,
        checked: isChecked,
        onCheck: handleToggle,
        onClick: handleToggle,
        disabled: isHideDisabled,
      });
    });

    return items;
  }, [
    searchTerm,
    areAllVisible,
    areSomeVisible,
    handleToggleSelectAll,
    filteredColumns,
    pendingColumnVisibility,
    setPendingColumnVisibility,
  ]);

  return (
    <SolyticsBox
      display="flex"
      flexDirection="column"
      height="100%"
      overflow="hidden"
    >
      {/* Search Section — fixed at top, not scrollable */}
      <SolyticsBox flexShrink={0} paddingX={2} paddingY={1}>
        <SolyticsSearchBar
          name="column-search"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          placeholder="Search"
          size="small"
          fullWidth
        />
      </SolyticsBox>

      {/* List Section — scrollable, stretches to fill remaining space */}
      {isNoColumnPresent ? (
        <SolyticsGrid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <SolyticsNoDataPresent message="No columns found" />
        </SolyticsGrid>
      ) : (
        <SolyticsBox flex={1} minHeight={0} overflow="auto" paddingX={2}>
          <SolyticsList
            disablePadding
            items={listItems}
            gap={1}
            listVirtualizationThreshold={listVirtualizationThreshold}
          />
        </SolyticsBox>
      )}
    </SolyticsBox>
  );
};

SolyticsTableShowHideColumns.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      header: PropTypes.string,
      disableHiding: PropTypes.bool,
    })
  ).isRequired,
  listVirtualizationThreshold: PropTypes.number,
  pendingColumnOrder: PropTypes.arrayOf(PropTypes.string),
  pendingColumnVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  setPendingColumnVisibility: PropTypes.func.isRequired,
};

export default SolyticsTableShowHideColumns;
